import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, MenuItem, Select, Stack, TextField,
} from '@mui/material';
import { SampleRequestService } from '../../services/sampleRequest.service';
import { AuthService } from '../../services/auth.service';
import { ISampleRequest, SampleStatus } from '../../interfaces/garments/sampleRequest.interface';
import { legalDecisionTargets } from './sampleRequestTransitions';
import { useToast } from '../../context/ToastContext';
import { enumLabel } from '../../utils/garmentsLabels';

interface Props {
    open: boolean;
    sampleRequest: ISampleRequest;
    onClose: () => void;
    onUpdated: (updated: ISampleRequest) => void;
}

/**
 * Buyer-decision move (PATCH `{id}/decision`) — offers only `legalDecisionTargets(currentStatus)`,
 * mirroring `SampleRequestService.DECISION_TRANSITIONS` on the backend (SENT ->
 * APPROVED/REJECTED/RESUBMIT_REQUIRED). `RESUBMITTED` never appears here either — same rule as
 * `SampleStatusDialog`.
 */
export function SampleDecisionDialog({ open, sampleRequest, onClose, onUpdated }: Props) {
    const toast = useToast();
    const options = legalDecisionTargets(sampleRequest.status);

    const [target, setTarget] = useState<SampleStatus | ''>('');
    const [buyerComments, setBuyerComments] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setTarget(options[0] ?? '');
            setBuyerComments(sampleRequest.buyerComments ?? '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sampleRequest.id]);

    const saveWhy = saving ? null
        : !target ? 'Pick the buyer decision.'
        : null;

    const handleSubmit = async () => {
        if (!target) return;
        const institutionId = AuthService.getSelectedInstitution();
        setSaving(true);
        try {
            const updated = await SampleRequestService.decision(institutionId, sampleRequest.id, {
                status: target,
                buyerComments: buyerComments || null,
            });
            toast.showSuccess(`Buyer decision recorded: ${target}`);
            onUpdated(updated);
            onClose();
        } catch (e) {
            toast.showApiError(e, 'Failed to record buyer decision');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ 'data-help-page': 'merchandising.sampleDecision.page' } as any}>
            <DialogTitle>Buyer Decision</DialogTitle>
            <DialogContent data-help-section="merchandising.sampleDecision.section.decision">
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <FormControl data-help="merchandising.sampleDecision.field.target" fullWidth size="small" disabled={saving || options.length === 0}>
                        <InputLabel id="sample-decision-dialog-target-label">Decision</InputLabel>
                        <Select
                            labelId="sample-decision-dialog-target-label"
                            label="Decision"
                            value={target}
                            onChange={e => setTarget(e.target.value as SampleStatus)}
                        >
                            {options.map(status => (
                                <MenuItem key={status} value={status}>{enumLabel(status)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        data-help="merchandising.sampleDecision.field.buyerComments"
                        label="Buyer Comments" size="small" fullWidth multiline rows={2} disabled={saving}
                        value={buyerComments} onChange={e => setBuyerComments(e.target.value)}
                        inputProps={{ maxLength: 1000 }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button data-help="merchandising.sampleDecision.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" data-help="merchandising.sampleDecision.submit" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} onClick={handleSubmit} disabled={saving || !target}>
                    Record Decision
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default SampleDecisionDialog;
